import { forwardRef, memo, useState } from 'react';
import PropTypes from 'prop-types';

import { Fade, IconButton, InputAdornment } from '@mui/material';
import { CalendarIcon, DatePicker } from '@mui/x-date-pickers-pro';

/**
 * Material UI DatePicker component wrapped to include custom styles and functionality.
 * Provides an input field with an icon to pick a date, using a dialog-style calendar popup.
 *
 * @param {Object} props - The properties passed to the DatePicker component.
 * @param {boolean} [props.error=false] - If true, shows an error state on the input element.
 * @param {boolean} [props.fullWidth=false] - If true, the input will take up the full width of its container.
 * @param {string} [props.helperText=''] - Optional helper text to display below the input.
 * @param {string} [props.size='medium'] - The size of the input element, can be 'small' or 'medium'.
 * @param {Object} [props.sx] - Custom Sx prop to pass styles directly into the input component.
 * @param {Object} [props.value=null] - The current value of the input, should be a Date object or null.
 * @param {Function} props.onChange - Function to call when the date changes. Receives the new date value as a parameter.
 * @param {Object} props.other - Other props that are passed directly to the MUI DatePicker component.
 */
const MDatePicker = forwardRef((props, ref) => {
  const {
    error = null,
    fullWidth = false,
    helperText = '',
    size = 'medium',
    sx,
    value = null,
    onChange,
    ...other
  } = props;

  const [open, setOpen] = useState(false);

  const handleChange = (newValue) => {
    onChange(newValue);
    setOpen(false);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <DatePicker
      {...other}
      open={open}
      ref={ref}
      TransitionComponent={Fade}
      showDaysOutsideCurrentMonth
      value={value}
      onChange={handleChange}
      onClose={handleClose}
      slotProps={{
        textField: {
          className: 'date-picker',
          fullWidth,
          size,
          sx,
          error,
          helperText,
          readOnly: false,
          InputProps: {
            endAdornment: (
              <InputAdornment position="end" onClick={handleOpen}>
                <IconButton edge="end">
                  <CalendarIcon />
                </IconButton>
              </InputAdornment>
            )
          }
        }
      }}
    />
  );
});

MDatePicker.propTypes = {
  error: PropTypes.bool,
  helperText: PropTypes.string,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(['medium', 'small']),
  sx: PropTypes.object,
  value: PropTypes.object,
  onChange: PropTypes.func
};

export default memo(MDatePicker);
